/**
 * @file The call recorder's pure parts (#2223 Phase 5, J1): the event queue
 * and its batching, how a page exit is named, and what the browser's WebRTC
 * stats say about the network path.
 *
 * Design context:
 * - The upload contract lives in `.claude/tracking/issue-2223/recorder-contract.md`
 *   (`POST /rooms/<id>/call-activity/`, a closed list of event kinds). Kinds
 *   here must stay that list: the server rejects anything else.
 * - Nothing in here records audio, video, chat, transcripts or anything a
 *   person typed (AC40). Events carry flags, levels, enum-like strings and
 *   the public IP the super admin's timeline is meant to show.
 * - Batches go out every ~10 s; the last one rides `fetch(keepalive)` on
 *   `pagehide`, which browsers cap at 64 KB of in-flight body for the whole
 *   page, so a keepalive flush is trimmed to a byte budget, oldest non-exit
 *   events first. Exit events (Leave / navigated away / page closed) are the
 *   point of the final flush and are never trimmed or suppressed, not even
 *   after the server said `truncated`.
 * - One request carries one `participant_sid`, so a rejoin (new sid) in the
 *   middle of a batch splits it into one request per sid, in order.
 * - Recovery code elsewhere in the module reports through `emitCallActivity`,
 *   a tiny bus, so those hooks need no knowledge of the recorder and their
 *   timing is untouched. With no recorder listening it does nothing.
 */

export type CallActivityKind =
  | "joined"
  | "mic_changed"
  | "camera_changed"
  | "left_button"
  | "navigated_away"
  | "page_closed"
  | "session_facts"
  | "network_changed"
  | "connection_state"
  | "path_changed"
  | "recovery"
  | "error"
  | "levels"
  | "capabilities"
  /** A listener couldn't hear a talking speaker (#2223 AC44): `speaker`, `state` start/end. */
  | "not_audible";

export type CallActivityData = Record<string, string | number | boolean | null>;

export interface CallActivityEvent {
  kind: CallActivityKind;
  /** Epoch ms, client clock. */
  at: number;
  data: CallActivityData;
}

export interface CallActivityBody {
  client_id: string;
  participant_sid: string | null;
  events: CallActivityEvent[];
}

/** What the server answered; null data = the request failed and may be retried. */
export interface CallActivityResult {
  /** The batch reached the server (accepted or deliberately refused) -- do not resend. */
  delivered: boolean;
  truncated: boolean;
}

export type CallActivitySender = (
  body: CallActivityBody,
  opts: { keepalive: boolean },
) => Promise<CallActivityResult>;

export const EXIT_KINDS: ReadonlySet<CallActivityKind> = new Set(["left_button", "navigated_away", "page_closed"]);

export const FLUSH_INTERVAL_MS = 10_000;
/** The server accepts at most this many events per request. */
export const MAX_EVENTS_PER_REQUEST = 500;
/** The server rejects an event whose `data` is larger than this. */
export const MAX_DATA_BYTES = 2048;
/** Final `pagehide` flush: under the browser's 64 KB keepalive quota. */
export const PAGEHIDE_BODY_BUDGET = 60_000;
/** Flush on going hidden: leaves room in the quota for the pagehide flush right after. */
export const HIDDEN_BODY_BUDGET = 30_000;
/** Pending events kept while the server is unreachable; oldest non-exit dropped first. */
export const MAX_PENDING = 3000;
/** `error.message` cap from the contract. */
export const MAX_ERROR_MESSAGE = 200;

const byteLength = (s: string) => new TextEncoder().encode(s).length;

// ── The bus ─────────────────────────────────────────────────────────────────

type Listener = (kind: CallActivityKind, data: CallActivityData) => void;
const listeners = new Set<Listener>();

/** Report something to the recorder, if one is running. Never throws. */
export function emitCallActivity(kind: CallActivityKind, data: CallActivityData = {}): void {
  for (const listener of listeners) {
    try {
      listener(kind, data);
    } catch {
      // A recorder fault must never reach the code that reported.
    }
  }
}

export function onCallActivity(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** An `error` event's data: a place and a short message, never user content. */
export function errorData(where: string, err: unknown): CallActivityData {
  const name = (err as { name?: unknown } | null)?.name;
  const message = (err as { message?: unknown } | null)?.message;
  const text = typeof name === "string" && name ? name : typeof message === "string" ? message : String(err);
  return { where, message: text.slice(0, MAX_ERROR_MESSAGE) };
}

// ── The queue ───────────────────────────────────────────────────────────────

interface Pending {
  sid: string | null;
  event: CallActivityEvent;
}

export class CallActivityQueue {
  private pending: Pending[] = [];
  private truncated = false;
  private ended = false;
  private flushing: Promise<void> | null = null;

  constructor(
    private readonly clientId: string,
    private readonly send: CallActivitySender,
    private readonly isEnabled: () => boolean,
  ) {}

  /** The server said this participant reached its cap: only exits go out from now on. */
  get isTruncated(): boolean {
    return this.truncated;
  }

  get size(): number {
    return this.pending.length;
  }

  push(kind: CallActivityKind, data: CallActivityData, sid: string | null, at: number = Date.now()): void {
    if (this.ended || !this.isEnabled()) return;
    const exit = EXIT_KINDS.has(kind);
    if (this.truncated && !exit) return;
    // The server would refuse the whole event; better to lose it here.
    if (byteLength(JSON.stringify(data)) > MAX_DATA_BYTES) return;
    this.pending.push({ sid, event: { kind, at, data } });
    if (this.pending.length > MAX_PENDING) this.dropOldestNonExit(this.pending.length - MAX_PENDING);
  }

  /** No more events after this one; a flush still sends what is queued. */
  end(): void {
    this.ended = true;
  }

  /**
   * Sends what is queued. A regular flush waits for one already running; a
   * keepalive flush (page going away) does not wait -- there is no time --
   * and sends at most `budget` bytes of body in total.
   */
  flush({ keepalive = false, budget = PAGEHIDE_BODY_BUDGET }: { keepalive?: boolean; budget?: number } = {}): Promise<void> {
    if (!this.isEnabled()) {
      this.pending = [];
      return Promise.resolve();
    }
    if (keepalive) return this.sendKeepalive(budget);
    if (this.flushing) return this.flushing;
    this.flushing = this.sendRegular().finally(() => {
      this.flushing = null;
    });
    return this.flushing;
  }

  private async sendRegular(): Promise<void> {
    const taken = this.pending;
    this.pending = [];
    const bodies = this.bodies(taken);
    for (let i = 0; i < bodies.length; i++) {
      const body = bodies[i];
      const result = await this.send(body, { keepalive: false }).catch(
        (): CallActivityResult => ({ delivered: false, truncated: false }),
      );
      if (result.truncated) this.markTruncated();
      if (!result.delivered) {
        // Unreachable: put this and every later batch back in front, in order.
        const unsent = bodies.slice(i).flatMap((b) => b.events.map((event) => ({ sid: b.participant_sid, event })));
        const back = this.truncated ? unsent.filter((p) => EXIT_KINDS.has(p.event.kind)) : unsent;
        this.pending = [...back, ...this.pending];
        if (this.pending.length > MAX_PENDING) this.dropOldestNonExit(this.pending.length - MAX_PENDING);
        return;
      }
    }
  }

  private async sendKeepalive(budget: number): Promise<void> {
    const taken = this.pending;
    this.pending = [];
    const bodies = trimToBudget(this.bodies(taken), this.clientId, budget);
    await Promise.all(
      bodies.map((body) =>
        this.send(body, { keepalive: true })
          .then((r) => {
            if (r.truncated) this.markTruncated();
          })
          .catch(() => {}),
      ),
    );
  }

  private markTruncated(): void {
    this.truncated = true;
    this.pending = this.pending.filter((p) => EXIT_KINDS.has(p.event.kind));
  }

  private dropOldestNonExit(count: number): void {
    let left = count;
    this.pending = this.pending.filter((p) => {
      if (left > 0 && !EXIT_KINDS.has(p.event.kind)) {
        left -= 1;
        return false;
      }
      return true;
    });
  }

  /** One body per run of the same sid, at most MAX_EVENTS_PER_REQUEST events each, in order. */
  private bodies(items: Pending[]): CallActivityBody[] {
    const out: CallActivityBody[] = [];
    for (const { sid, event } of items) {
      const last = out[out.length - 1];
      if (last && last.participant_sid === sid && last.events.length < MAX_EVENTS_PER_REQUEST) {
        last.events.push(event);
      } else {
        out.push({ client_id: this.clientId, participant_sid: sid, events: [event] });
      }
    }
    return out;
  }
}

/**
 * Fits bodies into `budget` bytes in total. The newest body is served first
 * (it holds the exit), and inside a body the oldest non-exit events go first.
 * Exit events are always kept.
 */
export function trimToBudget(bodies: CallActivityBody[], clientId: string, budget: number): CallActivityBody[] {
  const kept: CallActivityBody[] = [];
  let left = budget;
  for (let i = bodies.length - 1; i >= 0; i--) {
    const body = { ...bodies[i], client_id: clientId, events: [...bodies[i].events] };
    let size = byteLength(JSON.stringify(body));
    while (size > left) {
      const drop = body.events.findIndex((e) => !EXIT_KINDS.has(e.kind));
      if (drop === -1) break;
      size -= byteLength(JSON.stringify(body.events[drop])) + 1;
      body.events.splice(drop, 1);
    }
    if (body.events.length === 0) continue;
    size = byteLength(JSON.stringify(body));
    // Only exits left and still over: send them anyway (they are tiny).
    kept.unshift(body);
    left -= size;
    if (left <= 0) {
      // Older bodies only carry their exits, if any.
      for (let j = i - 1; j >= 0; j--) {
        const exits = bodies[j].events.filter((e) => EXIT_KINDS.has(e.kind));
        if (exits.length) kept.unshift({ ...bodies[j], client_id: clientId, events: exits });
      }
      break;
    }
  }
  return kept;
}

// ── How the page went away ──────────────────────────────────────────────────

/** A cross-document `navigate` this recent before `pagehide` counts as the cause. */
export const NAVIGATE_WINDOW_MS = 10_000;

/**
 * `navigated_away` when the page saw itself navigate to another document
 * (Navigation API) just before `pagehide`; otherwise `page_closed` -- a tab
 * or browser close, an address-bar navigation, or a browser without the
 * Navigation API. Browsers do not say which, so the contract has one
 * labelled event for all of those.
 */
export function exitKind(lastCrossDocumentNavigateAt: number | null, now: number): "navigated_away" | "page_closed" {
  return lastCrossDocumentNavigateAt !== null && now - lastCrossDocumentNavigateAt <= NAVIGATE_WINDOW_MS
    ? "navigated_away"
    : "page_closed";
}

/** True for a Navigation API `navigate` event that leaves this document. */
export function isCrossDocumentNavigate(event: unknown): boolean {
  const destination = (event as { destination?: { sameDocument?: unknown } } | null)?.destination;
  return destination?.sameDocument === false;
}

// ── What the stats say about the path ───────────────────────────────────────

export interface PathFacts {
  /** The selected candidate pair's id; changes when ICE moves to another path. */
  pairId: string;
  localType: string;
  remoteType: string;
  protocol: string;
  relay: boolean;
  /** The selected local candidate's address ("" if hidden). */
  localAddress: string;
  /** The srflx address (public IP as the STUN server saw it), or "unknown". */
  publicIp: string;
}

type Stat = Record<string, unknown> & { id: string; type: string };

function statsOf(report: RTCStatsReport): Stat[] {
  const out: Stat[] = [];
  report.forEach((s) => out.push(s as Stat));
  return out;
}

const str = (v: unknown, fallback = "unknown") => (typeof v === "string" && v ? v : fallback);

/**
 * The selected path from one peer connection's stats report: the transport's
 * `selectedCandidatePairId` (Chrome, Safari), else a pair flagged `selected`
 * (Firefox), else a nominated succeeded pair. `public_ip` is the selected
 * local candidate's address when it is srflx; when the path is host or relay,
 * any srflx candidate this connection gathered (same network, same STUN
 * answer) -- else "unknown".
 */
export function pathFactsFrom(report: RTCStatsReport | undefined): PathFacts | null {
  if (!report) return null;
  const stats = statsOf(report);
  const byId = new Map(stats.map((s) => [s.id, s]));
  let pair: Stat | undefined;
  const transport = stats.find((s) => s.type === "transport" && typeof s.selectedCandidatePairId === "string");
  if (transport) pair = byId.get(transport.selectedCandidatePairId as string);
  pair ??= stats.find((s) => s.type === "candidate-pair" && s.selected === true);
  pair ??= stats.find((s) => s.type === "candidate-pair" && s.nominated === true && s.state === "succeeded");
  if (!pair) return null;
  const local = byId.get(pair.localCandidateId as string);
  const remote = byId.get(pair.remoteCandidateId as string);
  if (!local) return null;
  const localType = str(local.candidateType);
  const localAddress = str(local.address ?? local.ip, "");
  const srflx = stats.find((s) => s.type === "local-candidate" && s.candidateType === "srflx");
  const publicIp =
    localType === "srflx" && localAddress ? localAddress : str(srflx?.address ?? srflx?.ip);
  return {
    pairId: pair.id,
    localType,
    remoteType: str(remote?.candidateType),
    protocol: str(local.relayProtocol ?? local.protocol),
    relay: localType === "relay" || remote?.candidateType === "relay",
    localAddress,
    publicIp,
  };
}

/** Whether this browser exposes the receipt the stall watchdog needs (`remote-inbound-rtp`). */
export function hasRemoteInbound(report: RTCStatsReport | undefined): boolean {
  return !!report && statsOf(report).some((s) => s.type === "remote-inbound-rtp");
}

/** Whether the connection gathered a srflx (public address) candidate. */
export function hasSrflx(report: RTCStatsReport | undefined): boolean {
  return !!report && statsOf(report).some((s) => s.type === "local-candidate" && s.candidateType === "srflx");
}

/** The received audio level (0-1) from a remote audio track's stats; null when not exposed. */
export function inboundAudioLevel(report: RTCStatsReport | undefined): number | null {
  if (!report) return null;
  let level: number | null = null;
  for (const s of statsOf(report)) {
    if (s.type === "inbound-rtp" && (s.kind ?? s.mediaType) === "audio" && typeof s.audioLevel === "number") {
      level = Math.max(level ?? 0, s.audioLevel);
    }
  }
  return level;
}

export function pathKey(p: PathFacts): string {
  return [p.pairId, p.localType, p.remoteType, p.protocol, p.relay].join("|");
}

export function pathData(p: PathFacts): CallActivityData {
  return { local_type: p.localType, remote_type: p.remoteType, protocol: p.protocol, relay: p.relay };
}

// ── Network type and the browser ────────────────────────────────────────────

/** `navigator.connection.type` where the browser has it (Chrome on Android), else "unknown". */
export function networkType(nav: Navigator = navigator): string {
  const type = (nav as Navigator & { connection?: { type?: unknown } }).connection?.type;
  return typeof type === "string" && type ? type : "unknown";
}

/**
 * Splits `describeBrowser`'s "<browser> on <system>" and names where the link
 * was opened: "<App> in-app browser", the regular browser's name (no
 * version), or "unknown".
 */
export function browserFacts(described: string): { browser: string; os: string; opened_in: string } {
  const at = described.lastIndexOf(" on ");
  const browser = at === -1 ? described : described.slice(0, at);
  const os = at === -1 ? "unknown" : described.slice(at + 4);
  let openedIn: string;
  if (/in-app browser$/i.test(browser)) openedIn = browser;
  else if (browser === "Other browser" || !browser) openedIn = "unknown";
  else openedIn = browser.replace(/\s+[\d.]+$/, "");
  return { browser, os, opened_in: openedIn };
}
